import { ColorOptions } from "@/src/types";
import { MainInner, StyledMainSection } from "@/styles/global";
import {
  Body,
  BodyCss,
  H1,
  TertiaryButtonCss,
  StyledWysiwyg,
} from "@/styles/typography";
import { GatsbyImage } from "gatsby-plugin-image";
import styled, { css, keyframes } from "styled-components";

type SectionProps = {
  $backgroundColor: ColorOptions;
  $backgroundType: "card" | "fill";
};

type InnerProps = {
  $backgroundColor: ColorOptions;
  $backgroundType: "card" | "fill";
  $textColor: ColorOptions;
};

const Section = styled(StyledMainSection)<SectionProps>`
  background-color: ${({ theme, $backgroundColor, $backgroundType }) =>
    $backgroundType === "card"
      ? "transparent"
      : $backgroundColor
        ? theme.colors[$backgroundColor]
        : "transparent"};

  margin: 0;
  padding-block: 124px;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
`;

const ImageTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 32px;
  margin-bottom: 16px;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
`;

const Image = styled(GatsbyImage)`
  width: 100%;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: none;
  }
`;

const TitleCss = css`
  color: ${({ theme }) => theme.colors.creme};
  font-family: "Herbus Wirepas Bold";
  font-size: 64px;
  font-style: normal;
  font-weight: 700;
  line-height: 110%; /* 70.4px */
  letter-spacing: 1.28px;
  text-transform: uppercase;
`;

const TitleH1 = styled.h1`
  ${TitleCss}
`;

const TitleH2 = styled.h2`
  ${TitleCss}
`;

const Subtitle = styled(Body)`
  color: ${({ theme }) => theme.colors.creme};
  font-size: 16px;
  margin-bottom: 40px;
`;

const HiddenFormOverlay = styled.div<{
  $hideForm: boolean;
  $backgroundColor: string;
}>`
  display: ${({ $hideForm }) => ($hideForm ? "block" : "none")};
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 250px;
  z-index: 10;
  cursor: pointer;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0),
    ${({ $backgroundColor, theme }) => theme.colors[$backgroundColor]}
  );
`;

const Form = styled.form<{ $hideForm: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;
  height: ${({ $hideForm }) => ($hideForm ? "350px" : "auto")};
  overflow: hidden;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ErrorMsg = styled(Body)`
  color: ${({ theme }) => theme.colors.red};
  font-size: 12px;
`;

const InputCss = css`
  ${BodyCss}
  font-size: 16px;
  padding: 12px 0 8px 0;
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.creme};
  background-color: transparent;
  color: ${({ theme }) => theme.colors.creme};
  position: relative;
  &::placeholder {
    color: ${({ theme }) => theme.colors.creme};
    position: absolute;
    top: 8px;
    transition: all 0.3s;
  }

  &:focus {
    outline: none;
    //placeholder to animate to border top

    &::placeholder {
      color: ${({ theme }) => theme.colors.creme};
      position: absolute;
      top: -2px;
      font-size: 10px;
    }
  }

  //when input is filled, keep placeholder on top and visible
  &:not(:placeholder-shown) {
    &::placeholder {
      top: -2px;
      font-size: 10px;
    }
  }

  //AUTOFILL STYLES
  &:-webkit-autofill {
    -webkit-box-shadow: 0 0 0 30px ${({ theme }) => theme.colors.black} inset;
    -webkit-text-fill-color: ${({ theme }) => theme.colors.creme};
  }

  //date picker arrow color
  &::-webkit-calendar-picker-indicator {
    filter: invert(1);
    cursor: pointer;
  }

  //when input is filled, or focus, move label up
  &:not(:placeholder-shown) + label,
  &:focus + label {
    top: -2px;
    font-size: 10px;
  }
`;

const Label = styled.label`
  ${BodyCss}
  position: absolute;
  left: 0px;
  top: 8px;
  color: ${({ theme }) => theme.colors.creme};
  transition: all 0.3s ease;
  pointer-events: none;
`;

const Input = styled.input`
  ${InputCss}
`;

const Textarea = styled.textarea`
  ${InputCss}
  height: 120px;
`;

const TextCss = css`
  ${BodyCss}
  color: ${({ theme }) => theme.colors.creme};
  font-size: 16px;
`;

const Text = styled.p`
  ${TextCss}
`;

const Submit = styled.button`
  ${TertiaryButtonCss}

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const ResponseMessage = styled(StyledWysiwyg)`
  p,
  a {
    color: ${({ theme }) => theme.colors.creme};
  }

  a {
    text-decoration: underline;
  }
`;

const Accordion = styled.details`
  display: flex;
  flex-direction: column;
  gap: 16px;
  transition: all 0.3s ease-in-out;
  overflow: hidden;

  &[open] {
    .accordion-arrow {
      transform: rotate(45deg);
    }
  }
`;

const AccordionTitle = styled.summary`
  ${TextCss}
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  width: 100%;
  padding: 8px 8px 8px 0;
`;

const AccordionArrow = styled.div`
  width: 8px;
  height: 8px;

  border-left: 1px solid ${({ theme }) => theme.colors.creme};
  border-top: 1px solid ${({ theme }) => theme.colors.creme};
  transform: rotate(-135deg);
  right: 8px;
  transition: all 0.3s ease-in-out;
`;

const RadioWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RadioTitle = styled(Text)``;

const Checkboxes = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-block: 8px 24px;
`;

const InnerContainer = styled.div<InnerProps>`
  display: flex;
  flex-direction: column;
  gap: 16px;

  background-color: ${({ theme, $backgroundColor }) =>
    $backgroundColor ? theme.colors[$backgroundColor] : "transparent"};
  border-radius: 21px;
  padding: ${({ $backgroundColor, $backgroundType }) =>
    $backgroundColor == "transparent"
      ? "20px 0"
      : $backgroundType === "fill"
        ? "20px 0"
        : "20px 24px"};

  ${Container} {
    padding-block: ${({ $backgroundType }) =>
      $backgroundType === "fill" ? "0px" : "40px"};
  }

  //text color
  ${Text}, ${TitleH1}, ${TitleH2}, ${Subtitle}, ${Input}, ${Label}, ${Textarea} {
    color: ${({ theme, $textColor }) =>
      $textColor ? theme.colors[$textColor] : "black"};
  }

  ${Input} {
    border-bottom: 1px solid
      ${({ theme, $textColor }) =>
        $textColor ? theme.colors[$textColor] : "black"};

    &::placeholder {
      color: ${({ theme, $textColor }) =>
        $textColor ? theme.colors[$textColor] : "black"};
    }
  }

  ${Textarea} {
    border-bottom: 1px solid
      ${({ theme, $textColor }) =>
        $textColor ? theme.colors[$textColor] : "black"};
  }

  ${AccordionTitle} {
    color: ${({ theme, $textColor }) =>
      $textColor ? theme.colors[$textColor] : "black"};
  }

  ${AccordionArrow} {
    border-left: 1px solid
      ${({ theme, $textColor }) =>
        $textColor ? theme.colors[$textColor] : "black"};
    border-top: 1px solid
      ${({ theme, $textColor }) =>
        $textColor ? theme.colors[$textColor] : "black"};
  }

  //input date picker
  input[type="date"] {
    color: ${({ theme, $textColor }) =>
      $textColor ? theme.colors[$textColor] : "black"};
  }
`;

const Inner = styled(MainInner)``;

const S = {
  Section,
  Inner,
  InnerContainer,
  Container,
  ImageTitleWrapper,
  Image,
  TitleH1,
  TitleH2,
  Subtitle,
  Form,
  HiddenFormOverlay,
  InputWrapper,
  ErrorMsg,
  Input,
  Label,
  Textarea,
  Text,
  Submit,
  ResponseMessage,
  Accordion,
  AccordionTitle,
  AccordionArrow,
  RadioWrapper,
  RadioTitle,
  Checkboxes,
};

export default S;
